import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { usePortal } from "@/context/PortalContext";
import portalApi from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IdentificationBadge, LockKey, Snowflake } from "@phosphor-icons/react";

export default function KaryawanLoginPage() {
  const { karyawan, loading, login } = usePortal();
  const [nik, setNik] = useState("");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  if (loading) return <div className="h-screen flex items-center justify-center text-slate-500">Memuat...</div>;
  if (karyawan) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!nik.trim()) { toast.error("NIK wajib diisi"); return; }
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) { toast.error("PIN harus 6 digit angka"); return; }
    setSubmitting(true);
    try {
      await login(nik.trim().toUpperCase(), pin);
      toast.success("Login berhasil");
    } catch (err) {
      // WP2: prefer standard {error:{message}} envelope, then legacy {detail}, then generic
      const data = err.response?.data;
      toast.error(formatApiError(data?.error?.message ?? data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#00558F] via-[#003E6B] to-[#001E38] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-6">
          <img src="/logo-gema.png" alt="GEMA" className="w-20 h-20 mx-auto rounded-2xl shadow-lg object-cover mb-3" data-testid="karyawan-login-logo"/>
          <h1 className="font-heading text-2xl font-bold text-white">Portal Karyawan</h1>
          <p className="text-sm text-cyan-200 mt-1">Login mandiri untuk absensi, payslip & pengajuan izin</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl p-6 shadow-2xl space-y-4" data-testid="karyawan-login-form">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5 tracking-wider uppercase">NIK Karyawan</label>
            <div className="relative">
              <IdentificationBadge size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                data-testid="login-nik"
                autoFocus required
                value={nik}
                onChange={e => setNik(e.target.value.toUpperCase())}
                placeholder="KRY-2026-0001"
                className="input w-full pl-10 font-mono-tab tracking-wider"
              />
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Format: KRY-YYYY-XXXX (tanya HRD kalau lupa)</div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5 tracking-wider uppercase">PIN 6 Digit</label>
            <div className="relative">
              <LockKey size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input
                data-testid="login-pin"
                type="password" inputMode="numeric" maxLength={6} required
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="••••••"
                className="input w-full pl-10 font-mono-tab text-center tracking-widest text-lg"
              />
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Login pertama: PIN default <span className="font-mono-tab font-bold">123456</span> — akan diminta ganti</div>
          </div>

          <button
            data-testid="login-submit"
            type="submit" disabled={submitting}
            className="w-full py-3.5 bg-[#00558F] hover:bg-[#003E6B] disabled:opacity-50 text-white rounded-lg font-bold text-sm transition-colors shadow-lg"
          >
            {submitting ? "Memproses..." : "Masuk Portal"}
          </button>

          <div className="text-center text-[11px] text-slate-500 pt-2 border-t border-slate-100 space-y-1">
            <button
              type="button"
              data-testid="btn-lupa-pin"
              onClick={()=>setShowForgot(true)}
              className="text-amber-600 hover:text-amber-700 font-semibold hover:underline"
            >Lupa PIN?</button>
            <div>Bukan karyawan? <Link to="/login" className="text-[#00558F] font-semibold hover:underline">Login ke ERP di sini</Link></div>
          </div>
        </form>

        <div className="text-center mt-4 text-[10px] text-cyan-200/60">
          GEMA ERP — Portal Karyawan
        </div>
      </div>
      {showForgot && <ForgotPinModal defaultNik={nik} onClose={()=>setShowForgot(false)}/>}
    </div>
  );
}


function ForgotPinModal({ defaultNik, onClose }) {
  const [nik, setNik] = useState(defaultNik || "");
  const [alasan, setAlasan] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!nik.trim()) { toast.error("NIK wajib diisi"); return; }
    setSaving(true);
    try {
      const r = await portalApi.post("/karyawan-auth/forgot-pin", { nik: nik.trim().toUpperCase(), alasan: alasan.trim() || null });
      toast.success(r.data.message || "Permintaan terkirim");
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="forgot-pin-modal">
      <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-sm space-y-4 shadow-2xl">
        <h2 className="font-heading text-lg font-bold text-[#00558F]">Kirim Permintaan Reset PIN</h2>
        {done ? (
          <>
            <p className="text-sm text-slate-600">Permintaan Anda sudah dikirim ke HRD. Setelah disetujui, PIN Anda akan direset ke <span className="font-mono-tab font-bold">123456</span>. Silakan login ulang dengan PIN default tersebut lalu buat PIN baru.</p>
            <button type="button" onClick={onClose} className="w-full py-2.5 bg-[#00558F] text-white rounded-lg font-semibold">Tutup</button>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-500">HRD akan mereset PIN Anda ke default. Setelah disetujui, login pakai PIN <span className="font-mono-tab font-bold">123456</span> dan buat PIN baru.</p>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">NIK Anda</label>
              <input data-testid="forgot-nik" required autoFocus value={nik} onChange={e=>setNik(e.target.value.toUpperCase())}
                className="input w-full font-mono-tab" placeholder="KRY-2026-0001"/>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Alasan (opsional)</label>
              <textarea data-testid="forgot-alasan" value={alasan} onChange={e=>setAlasan(e.target.value)}
                className="input w-full h-20 text-sm" placeholder="Lupa PIN, salah ganti PIN, HP hilang..."/>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Batal</button>
              <button data-testid="forgot-submit" type="submit" disabled={saving} className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-lg font-semibold">{saving?"Mengirim...":"Kirim ke HRD"}</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
